import { api, types } from "api";
// import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import React, { useCallback, useEffect, useState } from "react";
import { Box, Grid, styled, Tab, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GET_RESULT_FAIL } from "../../messages";
import { useAuthStatus } from "../../helpers/useAuthStatus";
import { ColorTabs } from "components/ColorTabs";
import { routes } from "routes";
import { StudentTable } from "./StudentTable";
import { Link, useParams } from "react-router-dom";
import {
	MultiUserResultProps,
	RoleType,
	UserResultProps,
	UUID,
} from "api/types";
import { DownloadDialog } from "../../components/DownloadDialog/DownloadDialog";

const PREFIX = "StudentTablePage";

const classes = {
	root: `${PREFIX}-root`,
	tabs: `${PREFIX}-tabs`,
	tabsBarBox: `${PREFIX}-tabsBarBox`,
	button: `${PREFIX}-button`,
	buttonList: `${PREFIX}-buttonList`,

	appBar: `${PREFIX}-appBar`,
	indicator: `${PREFIX}-indicator`,
	table: `${PREFIX}-table`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		flexGrow: 1,
		backgroundColor: theme.palette.background.paper,
	},
	[`& .${classes.tabs}`]: {
		color: "white",
	},
	[`& .${classes.tabsBarBox}`]: {
		backgroundColor: theme.palette.primary.main,
	},
	[`& .${classes.button}`]: {
		minWidth: theme.spacing(21),
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	[`& .${classes.buttonList}`]: {
		display: "flex",
	},

	[`& .${classes.appBar}`]: {
		backgroundColor: theme.palette.primary.main,
	},

	[`& .${classes.table}`]: {
		marginTop: theme.spacing(4),
	},
}));

export const StudentTablePage = (): JSX.Element => {
	const { role } = useAuthStatus();
	const [studentName, setStudentName] = useState("");
	// const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [, setErr] = useState("");
	const [students, setStudents] = useState<types.User[]>([]);
	const [allStudents, setAllStudents] = useState<types.User[]>([]);
	const [getResults, setGetResults] = useState<UUID[]>([]);
	const [loading, setLoading] = useState(true);
	const [userResultPropsList, setUserResultPropsList] =
		useState<MultiUserResultProps>({
			propsList: [],
		});
	const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
	// const [loading, setLoading] = useState(true);
	const params = useParams<{ groupId: UUID }>();

	const fetchGroupUser = useCallback((groupId: UUID) => {
		api
			.getGroupUsers(groupId, { role: "student" })
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				if (res.data === null || res.data.length === 0) {
					setStudents([]);
					return;
				}
				setStudents(res.data);
				setAllStudents(res.data);
			})
			.catch(() => {
				setErr(GET_RESULT_FAIL);
			});
	}, []);

	const fetchStudents = useCallback(() => {
		api
			.getUsers({ role: "student" })
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				if (res.data === null || res.data.length === 0) {
					setStudents([]);
					return;
				}
				setStudents(res.data);
				setAllStudents(res.data);
				// setLoading(false);
			})
			.catch(() => {
				setErr(GET_RESULT_FAIL);
			});
	}, []);

	const fetchMultiStudentResults = useCallback(() => {
		if (getResults.length === 0) {
			return;
		}

		api
			.getMultiStudentResults(getResults)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr("");
				let resPropsList: UserResultProps[] = [];
				res.data.forEach((studentRes) => {
					const resultProps: UserResultProps = {
						date: studentRes.date,
						name: studentRes.firstName + " " + studentRes.lastName,
						yearLevel: String(studentRes.yearLevel),
						totalAssignment: String(studentRes.totalAssignment),
						totalCorrect: String(studentRes.totalCorrect),
						totalAnswered: String(studentRes.totalAnswered),
						assignmentResults: studentRes.assignmentResultList,
						curriculumResults: studentRes.curriculumResultList,
						capabilityGrades: studentRes.capabilityGradeList,
						topicGrades: studentRes.topicGradeList,
						comments: studentRes.commentList,
						id: studentRes.userId,
					};
					resPropsList = [...resPropsList, resultProps];
				});
				setUserResultPropsList({ propsList: resPropsList });
				setLoading(false);
				setOpenDownloadDialog(true);
			})
			.catch(() => setErr(GET_RESULT_FAIL));
	}, [getResults]);

	useEffect(() => {
		if (params.groupId !== undefined) {
			fetchGroupUser(params.groupId);
		} else {
			if (role === "admin" || role === "teacher" || role === "admin-teacher") {
				fetchStudents();
			}
		}
	}, [role, fetchStudents, fetchGroupUser, params.groupId]);

	useEffect(() => {
		if (role === "admin" || role === "teacher" || role === "admin-teacher") {
			fetchMultiStudentResults();
		}
	}, [role, fetchMultiStudentResults]);

	const onDownload = (ids: string[]): void => {
		setGetResults(ids);
	};

	const onCheckOpen = (open: boolean): void => {
		setOpenDownloadDialog(open);
	};

	return (
		<Root className={classes.root}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				className={classes.tabsBarBox}
			>
				<ColorTabs
					value={routes._.result._.Students.path}
					variant="scrollable"
					scrollButtons
				>
					<Tab
						label="GROUP LIST"
						value={routes._.result._.Groups.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.result._.Groups.path}
					/>
					<Tab
						label="STUDENT LIST"
						value={routes._.result._.Students.path}
						sx={{ color: "white" }}
						classes={{
							root: classes.tabs,
						}}
						component={Link}
						to={routes._.result._.Students.path}
					/>
				</ColorTabs>
			</Box>

			<StudentTable students={students} onDownload={onDownload}>
				<Grid item xs={1}>
					{!loading && (
						<DownloadDialog
							role={role as RoleType}
							loading={false}
							resultToBeRendered={userResultPropsList}
							templateType={"individual"}
							checkOpen={openDownloadDialog}
							onCheckOpen={onCheckOpen}
						/>
					)}
				</Grid>
				<Grid item xs={4}>
					{/* student search box */}
					<TextField
						id="group-name"
						label="Student Name"
						variant="outlined"
						value={studentName}
						onChange={(e) => {
							const searchName = e.target.value;
							if (searchName.length < studentName.length) {
								// monitor backspace
								setStudents(allStudents);
							} else {
								let searchStudents: Array<types.User> = [];
								const regex = /^ *$/;
								// a name is null or empty string or all whitespace
								if (regex.exec(searchName) !== null) {
									setStudents(allStudents);
								} else {
									students.forEach((student) => {
										const name = student.firstName + " " + student.lastName;
										if (
											name
												.toLowerCase()
												.includes(searchName.trim().toLowerCase())
										) {
											searchStudents = [...searchStudents, student];
										}
									});
									setStudents(searchStudents);
								}
							}
							setStudentName(searchName);
						}}
						fullWidth
						InputProps={{
							endAdornment: <SearchIcon />,
						}}
					/>
				</Grid>
			</StudentTable>
		</Root>
	);
};
